import { supabase } from '@/integrations/supabase/client';

export interface Tenant {
  id: string;
  name: string;
  code: string;
  logo_url: string | null;
  is_active: boolean;
  microsoft_tenant_id: string | null;
  settings: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface TenantMembership {
  id: string;
  tenant_id: string;
  user_id: string;
  status: 'pending' | 'approved' | 'rejected';
  tenant?: Pick<Tenant, 'id' | 'name' | 'code'>;
}

export interface TenantMemberWithProfile {
  id: string;
  user_id: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  profile: {
    id: string;
    email: string;
    full_name: string | null;
    department: string | null;
    job_title: string | null;
  };
}


export async function fetchTenantById(tenantId: string): Promise<Tenant | null> {
  const { data, error } = await supabase
    .schema('core')
    .from('tenants')
    .select('*')
    .eq('id', tenantId)
    .maybeSingle();

  if (error) throw error;
  return data as Tenant | null;
}

export async function fetchAllTenants(): Promise<Tenant[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('tenants')
    .select('*')
    .order('name');

  if (error) throw error;
  return data as Tenant[];
}

export async function fetchUserTenantMemberships(): Promise<TenantMembership[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('tenant_memberships')
    .select('id, tenant_id, user_id, status, tenant:tenants(id, name, code)')
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data as TenantMembership[];
}

export async function fetchTenantMembers(tenantId: string): Promise<TenantMemberWithProfile[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('tenant_memberships')
    .select(`
      id,
      user_id,
      status,
      created_at,
      profile:profiles!tenant_memberships_user_id_fkey(id, email, full_name, department, job_title)
    `)
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as TenantMemberWithProfile[];
}

export async function createTenantMembership(tenantId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('tenant_memberships')
    .insert({ tenant_id: tenantId, user_id: userId, status: 'pending' });

  if (error) throw error;
}
